import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

/**
 * API base path without editing built files per environment (if/else only).
 * - ng serve (4200/4201): /api (proxy rewrites to XAMPP /wiztasks/api).
 * - Live wiztasks.wizcode.lk: /api (API at site root).
 * - XAMPP subfolder: URL path or <base href> under /wiztasks → /wiztasks/api.
 * - Otherwise: /api (root / vhost).
 */
function apiBasePath(): string {
  if (typeof window === 'undefined') return '/api';
  const { hostname, port, pathname } = window.location;
  const h = hostname.toLowerCase();
  if (port === '4200' || port === '4201') {
    return '/api';
  }
  if (h === 'wiztasks.wizcode.lk' || h.endsWith('.wiztasks.wizcode.lk')) {
    return '/api';
  }
  const baseHrefPath = (() => {
    const raw = document.querySelector('base')?.getAttribute('href') || '/';
    try {
      const u = new URL(raw, window.location.origin);
      let bp = u.pathname || '/';
      if (bp.length > 1 && bp.endsWith('/')) bp = bp.slice(0, -1);
      return bp;
    } catch {
      return '/';
    }
  })();
  if (baseHrefPath !== '/' && (baseHrefPath === '/wiztasks' || baseHrefPath.startsWith('/wiztasks/'))) {
    return '/wiztasks/api';
  }
  const p = pathname || '/';
  if (p === '/wiztasks' || p.startsWith('/wiztasks/')) {
    return '/wiztasks/api';
  }
  return '/api';
}

/**
 * Allow /api → /wiztasks/api retry on localhost/LAN (including ng serve :4200/:4201).
 * proxy.conf.json must proxy both /api and /wiztasks/api to XAMPP so retries stay same-origin.
 */
function canFallbackToWiztasksApi(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1') return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  return false;
}

/** When /api returns non-JSON (or HTML error body) on localhost/LAN, retry once with /wiztasks/api (XAMPP subfolder). */
function shouldRetryWithWiztasksApi(res: ApiResponse<unknown>, attemptedBase: string): boolean {
  if (attemptedBase !== '/api') return false;
  if (!canFallbackToWiztasksApi()) return false;
  if (res.status === true) return false;
  const m = res.message || '';
  return (
    m.includes('Server returned HTML') ||
    m.includes('web page instead of JSON') ||
    m.includes('Empty response from server') ||
    m.includes('Response was not valid JSON from the API')
  );
}

const JSON_HEADERS = new HttpHeaders({ 'Content-Type': 'application/json' });

function apiErrorMessage(err: unknown): string {
  const e = err as { status?: number; statusText?: string; error?: { message?: string } | string };
  let msg = 'Request failed';
  if (e?.error) {
    if (typeof e.error === 'string') {
      const t = e.error.trim();
      if (t.startsWith('<!DOCTYPE') || t.toLowerCase().startsWith('<html')) {
        msg =
          'Server returned a web page instead of JSON (wrong API URL or SPA fallback). Check that the API base path matches your deployment.';
      } else if (t.startsWith('{') || t.startsWith('[')) {
        try {
          const parsed = JSON.parse(t) as { message?: string };
          if (parsed && typeof parsed.message === 'string' && parsed.message.trim() !== '') {
            msg = parsed.message;
          } else {
            msg = t;
          }
        } catch {
          msg = t;
        }
      } else {
        msg = e.error;
      }
    } else if (typeof e.error?.message === 'string') msg = e.error.message;
  }
  const status = e?.status;
  if (status === 0) msg += ' — cannot reach server (check CORS, network, or API URL)';
  return msg;
}

/** When PHP appends an error page after a valid JSON envelope, parse the leading object only. */
function parseLeadingJsonObject<T>(text: string): ApiResponse<T> | null {
  const start = text.indexOf('{');
  if (start < 0) return null;
  let depth = 0;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) {
        try {
          return JSON.parse(text.slice(start, i + 1)) as ApiResponse<T>;
        } catch {
          return null;
        }
      }
    }
  }
  return null;
}

/** Parse JSON API envelope from raw text (avoids HttpClient JSON parse errors on 200 + HTML). */
function parseApiEnvelopeJson<T>(text: string): ApiResponse<T> {
  const t = (text ?? '').trim();
  if (t === '') {
    return { status: false, data: null as T, message: 'Empty response from server' };
  }
  if (t.startsWith('<!DOCTYPE') || t.toLowerCase().startsWith('<html')) {
    return {
      status: false,
      data: null as T,
      message:
        'Server returned HTML instead of JSON (wrong API URL or SPA fallback). Use /api on root installs or /wiztasks/api when the app URL starts with /wiztasks/.',
    };
  }
  try {
    return JSON.parse(t) as ApiResponse<T>;
  } catch {
    const leading = parseLeadingJsonObject<T>(t);
    if (leading) {
      return leading;
    }
    return { status: false, data: null as T, message: 'Response was not valid JSON from the API' };
  }
}

export interface ApiResponse<T = unknown> {
  status: boolean;
  data: T;
  message: string;
}

export interface UploadProgressEvent<T = unknown> {
  type: 'progress' | 'response';
  progress?: number;
  response?: ApiResponse<T>;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private http: HttpClient) {}

  /** One retry with /wiztasks/api when /api returns non-JSON on local Apache/XAMPP. */
  private requestWithLocalFallback<T>(
    primaryBase: string,
    exec: (base: string) => Observable<ApiResponse<T>>,
    allowRetry: boolean,
  ): Observable<ApiResponse<T>> {
    return exec(primaryBase).pipe(
      switchMap((res) => {
        if (allowRetry && shouldRetryWithWiztasksApi(res, primaryBase)) {
          return exec('/wiztasks/api');
        }
        return of(res);
      }),
    );
  }

  get<T>(path: string, params?: Record<string, string | number>): Observable<ApiResponse<T>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        httpParams = httpParams.set(k, String(v));
      });
    }
    const primary = apiBasePath();
    const exec = (base: string) =>
      this.http.get(base + path, { params: httpParams, withCredentials: true, responseType: 'text' }).pipe(
        map((text) => parseApiEnvelopeJson<T>(text)),
        catchError((e) => of({ status: false, data: null as T, message: apiErrorMessage(e) })),
      );
    return this.requestWithLocalFallback(primary, exec, true);
  }

  /** POST with FormData (e.g. file upload). Do not set Content-Type so browser sets multipart boundary. */
  postFormData<T>(path: string, formData: FormData): Observable<ApiResponse<T>> {
    const primary = apiBasePath();
    const exec = (base: string) =>
      this.http.post(base + path, formData, { withCredentials: true, responseType: 'text' }).pipe(
        map((text) => parseApiEnvelopeJson<T>(text)),
        catchError((e) => of({ status: false, data: null as T, message: apiErrorMessage(e) })),
      );
    return this.requestWithLocalFallback(primary, exec, false);
  }

  /** POST FormData with upload progress callbacks. */
  postFormDataWithProgress<T>(path: string, formData: FormData): Observable<UploadProgressEvent<T>> {
    const primaryBase = apiBasePath();
    const uploadWithBase = (base: string): Observable<UploadProgressEvent<T>> => {
      const url = base + path;
      return this.http
        .post(url, formData, {
          withCredentials: true,
          observe: 'events',
          reportProgress: true,
          responseType: 'text',
        })
        .pipe(
          map((event: HttpEvent<string>) => {
            if (event.type === HttpEventType.UploadProgress) {
              const total = event.total ?? 0;
              const loaded = event.loaded ?? 0;
              const progress = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
              return { type: 'progress', progress } as UploadProgressEvent<T>;
            }
            if (event.type === HttpEventType.Response) {
              const body = event.body ?? '';
              const parsed = parseApiEnvelopeJson<T>(body);
              return {
                type: 'response',
                response: parsed,
              } as UploadProgressEvent<T>;
            }
            return { type: 'progress', progress: 0 } as UploadProgressEvent<T>;
          }),
          catchError((e) => {
            return of({
              type: 'response',
              response: { status: false, data: null as T, message: apiErrorMessage(e) },
            } as UploadProgressEvent<T>);
          }),
        );
    };

    // Never retry uploads: a non-JSON response may still mean the file was stored.
    return uploadWithBase(primaryBase);
  }

  post<T>(path: string, body: unknown): Observable<ApiResponse<T>> {
    const primary = apiBasePath();
    const exec = (base: string) =>
      this.http.post(base + path, body, { withCredentials: true, headers: JSON_HEADERS, responseType: 'text' }).pipe(
        map((text) => parseApiEnvelopeJson<T>(text)),
        catchError((e) => of({ status: false, data: null as T, message: apiErrorMessage(e) })),
      );
    return this.requestWithLocalFallback(primary, exec, false);
  }

  put<T>(path: string, body: unknown): Observable<ApiResponse<T>> {
    const primary = apiBasePath();
    const exec = (base: string) =>
      this.http.put(base + path, body, { withCredentials: true, headers: JSON_HEADERS, responseType: 'text' }).pipe(
        map((text) => parseApiEnvelopeJson<T>(text)),
        catchError((e) => of({ status: false, data: null as T, message: apiErrorMessage(e) })),
      );
    return this.requestWithLocalFallback(primary, exec, false);
  }

  patch<T>(path: string, body: unknown): Observable<ApiResponse<T>> {
    const primary = apiBasePath();
    const exec = (base: string) =>
      this.http.patch(base + path, body, { withCredentials: true, headers: JSON_HEADERS, responseType: 'text' }).pipe(
        map((text) => parseApiEnvelopeJson<T>(text)),
        catchError((e) => of({ status: false, data: null as T, message: apiErrorMessage(e) })),
      );
    return this.requestWithLocalFallback(primary, exec, false);
  }

  delete<T>(path: string, params?: Record<string, string | number>): Observable<ApiResponse<T>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        httpParams = httpParams.set(k, String(v));
      });
    }
    const primary = apiBasePath();
    const exec = (base: string) =>
      this.http.delete(base + path, { params: httpParams, withCredentials: true, responseType: 'text' }).pipe(
        map((text) => parseApiEnvelopeJson<T>(text)),
        catchError((e) => of({ status: false, data: null as T, message: apiErrorMessage(e) })),
      );
    return this.requestWithLocalFallback(primary, exec, false);
  }
}
